// Single source of truth for the predicate fields exposed in the Segment Builder.
// The backend FIELD_MAP in aamcirc/segments/predicate.py is authoritative for
// what the compiler will accept; this list is a curated subset with UI metadata
// (label, kind, enum options) so we can render appropriate inputs.

export type FieldKind = 'enum' | 'boolean' | 'date' | 'numeric' | 'text'

export type FieldSpec = {
  key: string
  label: string
  kind: FieldKind
  options?: Array<{ value: string; label: string }>
}

// SubscriptionStatus from aamcirc/db/models.py::SubscriptionStatus.
// Display order puts common statuses first.
const STATUS_OPTIONS = [
  { value: 'Active', label: 'Active' },
  { value: 'Expired', label: 'Expired' },
  { value: 'Cancelled', label: 'Cancelled' },
  { value: 'On Hold', label: 'On Hold' },
  { value: 'Bad Address', label: 'Bad Address' },
  { value: 'Bad Debt', label: 'Bad Debt' },
  { value: 'Deceased', label: 'Deceased' },
  { value: 'Other', label: 'Other' },
]

// Publication slugs from aamcirc/config.py::PUBLICATION_SLUGS.
const PUBLICATION_OPTIONS = [
  { value: 'rc_car_action', label: 'RC Car Action' },
  { value: 'flight_journal', label: 'Flight Journal' },
  { value: 'model_airplane_news', label: 'Model Airplane News' },
  { value: 'diecast', label: 'Die Cast X' },
]

const SUBSCRIPTION_TYPE_OPTIONS = [
  { value: 'New', label: 'New' },
  { value: 'Renewal', label: 'Renewal' },
  { value: 'Gift', label: 'Gift' },
]

// Observed values from the SimpleCirc CSV / dashboard code.
const AUTO_RENEW_STATUS_OPTIONS = [
  { value: 'CHARGED', label: 'Charged OK' },
  { value: 'RECHARGE FAILED', label: 'Recharge Failed' },
  { value: 'THIRD CHARGE FAILED', label: 'Third Charge Failed' },
  { value: 'FIFTH CHARGE FAILED', label: 'Fifth Charge Failed' },
  { value: 'FINAL CHARGE FAILED', label: 'Final Charge Failed' },
]

// Common payment types observed in production; not enum-enforced at the DB
// level so new values may appear. The dropdown covers today's reality; ops
// can always fall back to typing a value via the "in" op if a new type shows up.
const PAYMENT_TYPE_OPTIONS = [
  { value: 'Auto Renewal', label: 'Auto Renewal' },
  { value: 'Credit Card', label: 'Credit Card' },
  { value: 'Check', label: 'Check' },
  { value: 'PayPal Button', label: 'PayPal' },
  { value: 'Invoice', label: 'Invoice' },
  { value: 'Agency', label: 'Agency' },
  { value: 'Other', label: 'Other' },
]

const PAYMENT_CUSTOM_SOURCE_OPTIONS = [
  { value: 'AGENCY', label: 'Agency' },
  { value: 'BILLING', label: 'Billing' },
  { value: 'INTERNET', label: 'Internet' },
  { value: 'RENEWALS', label: 'Renewals' },
]

export const FIELD_SPECS: FieldSpec[] = [
  // Publication
  { key: 'subscription.publication_slug', label: 'Publication', kind: 'enum', options: PUBLICATION_OPTIONS },

  // Subscription core
  // NOTE: subscription.started_at and subscription.expired_at are intentionally
  // omitted. The CSV importer currently maps them from "Payment Start Date" /
  // "Payment Expire Date" (credit-card dates), not subscription-term dates.
  // For date-range queries use `current_period_end` — it's the authoritative
  // "this term runs through / ended on" field mapped from "Subscription
  // Expiration Date" in the CSV.
  { key: 'subscription.status', label: 'Subscription status', kind: 'enum', options: STATUS_OPTIONS },
  { key: 'subscription.issues_remaining', label: 'Months remaining', kind: 'numeric' },
  { key: 'subscription.current_period_end', label: 'Period end', kind: 'date' },
  { key: 'subscription.never_expires', label: 'Never expires', kind: 'boolean' },
  { key: 'subscription.do_not_renew', label: 'Do not renew', kind: 'boolean' },

  // Renewal / channel
  { key: 'subscription.subscription_type', label: 'Subscription type', kind: 'enum', options: SUBSCRIPTION_TYPE_OPTIONS },
  { key: 'subscription.auto_renew', label: 'Auto-renew', kind: 'boolean' },
  { key: 'subscription.auto_renew_price', label: 'Auto-renew price', kind: 'numeric' },
  { key: 'subscription.auto_renew_status', label: 'Auto-renew status', kind: 'enum', options: AUTO_RENEW_STATUS_OPTIONS },

  // Payment
  { key: 'subscription.payment_type', label: 'Payment type', kind: 'enum', options: PAYMENT_TYPE_OPTIONS },
  { key: 'subscription.payment_custom_source', label: 'Payment source', kind: 'enum', options: PAYMENT_CUSTOM_SOURCE_OPTIONS },
  { key: 'subscription.last_payment_date', label: 'Last payment date', kind: 'date' },

  // Subscriber contact
  { key: 'subscriber.email', label: 'Email', kind: 'text' },
  { key: 'subscriber.first_name', label: 'First name', kind: 'text' },
  { key: 'subscriber.last_name', label: 'Last name', kind: 'text' },
  { key: 'subscriber.state', label: 'State', kind: 'text' },
  { key: 'subscriber.country', label: 'Country', kind: 'text' },

  // Subscriber audit
  { key: 'subscriber.created_at', label: 'Joined at', kind: 'date' },
  { key: 'subscriber.email_suppressed_at', label: 'Email suppressed at', kind: 'date' },
]

export function getFieldSpec(key: string): FieldSpec | undefined {
  return FIELD_SPECS.find((f) => f.key === key)
}


// Story types from aamcirc/lab/content/ai_enrichment.py::STORY_TYPES.
const STORY_TYPE_OPTIONS = [
  { value: 'firsthand_account', label: 'First-hand account' },
  { value: 'iconic_aircraft_vehicle', label: 'Iconic aircraft / vehicle' },
  { value: 'historical_deep_dive', label: 'Historical deep-dive' },
  { value: 'combat_story', label: 'Combat story' },
  { value: 'profile', label: 'Profile' },
  { value: 'technical_analysis', label: 'Technical analysis' },
  { value: 'how_to', label: 'How-to' },
  { value: 'review', label: 'Review' },
  { value: 'product_announcement', label: 'Product announcement' },
  { value: 'interview', label: 'Interview' },
  { value: 'editorial', label: 'Editorial' },
  { value: 'news', label: 'News' },
  { value: 'other', label: 'Other' },
]


// Fields available inside an `engaged` node's `where` subtree. The backend
// counterpart is ARTICLE_FIELD_MAP in aamcirc/segments/predicate.py — keep
// these in sync. Era is intentionally text (not enum) until the backend
// taxonomy is constrained.
export const ARTICLE_FIELD_SPECS: FieldSpec[] = [
  { key: 'article.story_type',       label: 'Story type',       kind: 'enum', options: STORY_TYPE_OPTIONS },
  { key: 'article.era',              label: 'Era',              kind: 'text' },
  { key: 'article.ai_tags',          label: 'AI tag',           kind: 'text' },
  { key: 'article.publication_slug', label: 'Publication',      kind: 'enum', options: PUBLICATION_OPTIONS },
  { key: 'article.member_only',      label: 'Member-only',      kind: 'boolean' },
  { key: 'article.post_date',        label: 'Article post date', kind: 'date' },
]

export function getArticleFieldSpec(key: string): FieldSpec | undefined {
  return ARTICLE_FIELD_SPECS.find((f) => f.key === key)
}

// Ops that don't need a value.
export const NULLARY_OPS = new Set(['is_null', 'is_not_null'])
// Ops that take an array of values.
export const LIST_OPS = new Set(['in', 'not_in'])
// Ops that make no sense on certain kinds — used to filter the op dropdown
// down to options that fit the selected field. Booleans don't need ordering;
// enums don't need contains/starts_with; dates don't need contains.
export const OPS_BY_KIND: Record<FieldKind, string[]> = {
  enum: ['eq', 'neq', 'in', 'not_in', 'is_null', 'is_not_null'],
  boolean: ['eq', 'neq', 'is_null', 'is_not_null'],
  date: ['eq', 'neq', 'lt', 'lte', 'gt', 'gte', 'in_month', 'is_null', 'is_not_null'],
  numeric: ['eq', 'neq', 'lt', 'lte', 'gt', 'gte', 'in', 'not_in', 'is_null', 'is_not_null'],
  text: ['eq', 'neq', 'in', 'not_in', 'contains', 'starts_with', 'is_null', 'is_not_null'],
}
